class ProductVariantSelect extends HTMLElement {
  abortController;

  connectedCallback() {
    this.initOptions();
  }

  /**
   * Initialize the options.
   */
  initOptions() {
    this.options = this.querySelectorAll('[data-variant-id]');
    this.handle = this.dataset.handle;
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.qsProductId;
    this.input = this.querySelector('input[name="id"]');

    this.addEventListener('change', (event) => {
      const target = event.target.closest('[data-variant-id]');
      if (target && !this.abortController) this.onVariantChange({ target });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target.closest('[data-variant-id]');
      if (target && !this.abortController) {
        event.preventDefault();
        target.checked = true;
        this.onVariantChange({ target });
      }
    });

    const originalSelector = this.querySelector('[data-stock-indicator][aria-hidden="false"]');
    this.originalStockState = originalSelector?.dataset?.stockIndicator;
    this.stockIndicators = this.querySelectorAll('[data-stock-indicator]');

    this.productOptionLabels = this.querySelectorAll('[data-product-option-label]');
    this.productOptionLabels.forEach((label) => {
      const originalTitle = label?.closest('fieldset')?.querySelector('legend')?.textContent;
      const hoverTitle = label?.closest('[data-product-option]')?.querySelector('input')?.value;

      label.addEventListener('mouseenter', () => {
        this.handleOptionHover(label, hoverTitle);
      });

      label.addEventListener('mouseleave', () => {
        this.handleOptionHover(label, originalTitle, this.originalStockState);
      });

      label.addEventListener('click', () => {
        const originalState = label?.dataset.state;
        this.originalStockState = originalState;
        this.handleOptionHover(label, hoverTitle, originalState);
      });
    });
  }

  /**
   * Handle variant change.
   * @param {Event} event - The event object.
   */
  async onVariantChange({ target }) {
    const { variantId } = target.dataset;

    const params = this.createParams(variantId);

    this.input.value = variantId;
    this.input.dispatchEvent(new Event('change'));

    // Only update URL when explicitly configured (determined by Liquid logic)
    const shouldUpdateURL = this.dataset.updateUrl === 'true';

    if (shouldUpdateURL) {
      this.updateURL(variantId);
    }

    // Immediately uncheck all other checkboxes within the same fieldset to improve perceived speed.
    this.updateFieldset(target);

    if (this.abortController) return;
    this.abortController = new AbortController();

    try {
      const url = `/products/${this.handle}?${params}`;
      const response = await fetch(url, {
        signal: this.abortController.signal,
      });

      const text = await response.text();
      const parsedHTML = new DOMParser().parseFromString(text, 'text/html');

      const newVariantSelect = parsedHTML.querySelector('product-variant-select').cloneNode(true);
      // Replace the url with the original url in case the product is in a dynamic section, otherwise the url will be updated to the new variant url that conflicts
      newVariantSelect.dataset.updateUrl = this.dataset.updateUrl;
      this.replaceWith(newVariantSelect);

      publish(`variant:changed:${this.productId}:${this.sectionId}`, {
        variantId,
        html: parsedHTML,
      });

      // Scroll to top of section on mobile view, only when URL updates are enabled (i.e. main product page)
      const section = document.querySelector(`[data-section-id="${this.sectionId}"]`);
      const top = section ? section.offsetTop : 0;
      window.scrollTo({ top, behavior: 'smooth' });
    } catch (error) {
      if (error.name === 'AbortError')
        return console.error('Variant change request was aborted due to a new variant selection.');

      console.error(error.message);
      window.toast.error(window.toast_translations.product.variant_error);
    } finally {
      this.abortController = null;
      document.querySelector(`#${target.id}`).focus();
    }
  }

  /**
   * Update URL
   * @param {String} value - URL value
   */
  updateURL(value) {
    const { searchParams } = new URL(window.location);
    searchParams.set('variant', value);

    // Replace the current history state with the new search params, without reloading the page
    history.replaceState(null, '', `${window.location.pathname}?${searchParams}`);
  }

  /**
   * Create the params.
   * @param {String} variantId - The variant ID.
   * @returns {String} The params.
   */
  createParams(variantId) {
    return [`section_id=${this.sectionId}`, `variant=${variantId}`].join('&');
  }

  /**
   * @method Update the fieldset.
   * @param {HTMLElement} target - The target element.
   */
  updateFieldset(target) {
    const fieldset = target.closest('fieldset');
    if (!fieldset) return;

    fieldset.querySelectorAll('[data-variant-id]').forEach((option) => {
      option.checked = option === target;
    });
  }

  handleOptionHover(option, newTitle, originalState) {
    const titleTarget = option.closest('fieldset').querySelector('legend');
    if (!titleTarget) return;
    titleTarget.innerHTML = newTitle;

    this.stockIndicators.forEach((indicator) => {
      indicator.setAttribute('aria-hidden', 'true');
      if (indicator.dataset.stockIndicator === (originalState || option.dataset.state)) {
        indicator.setAttribute('aria-hidden', 'false');
      }
    });
  }
}

customElements.define('product-variant-select', ProductVariantSelect);
